<template>
    <card title="Details" class="breakdown" layout="table" v-if="hasReportResults">
        <template #default>
            <div class="columns">
                <div class="column is-6">
                    <breadcrumb :crumbs="breadcrumbs"></breadcrumb>
                </div>
                <div class="column is-6 has-text-right is-flex is-justify-content-flex-end">
                    <o-button class="mr-4" icon-left="history" v-if="currentBreakdown.breakdownFilter" @click="Revert">
                        Clear all filters
                    </o-button>
                    <div class="mr-4">
                        <o-input v-model="search" placeholder="Search..." type="search" icon="search" expanded @input="Search"></o-input>
                    </div>
                    <o-select v-model="view">
                        <option :key="option.key" :value="option.key" v-for="option in options.breakdowns">
                            {{ option.text }}
                        </option>
                    </o-select>
                </div>
            </div>
            <div class="tab-section mb-5">
                <o-tabs v-model="view" :multiline="true" :expanded="true">
                    <o-tab-item value="region" label="Region"></o-tab-item>
                    <o-tab-item value="license" label="License"></o-tab-item>
                    <o-tab-item value="location" label="Location"></o-tab-item>
                    <o-tab-item value="fullName" label="Partner"></o-tab-item>
                    <o-tab-item value="assignmentId" label="Assignment"></o-tab-item>
                    <o-tab-item value="company" label="Company"></o-tab-item>
                    <o-tab-item value="parent" label="Parent"></o-tab-item>
                    <o-tab-item value="function" label="Function"></o-tab-item>
                    <o-tab-item value="industry" label="Industry"></o-tab-item>
                    <o-tab-item value="reportGroup" label="Report Group"></o-tab-item>
                    <o-tab-item value="industryGroup" :label="dashboard ? 'Industry Group' : 'Practice Group'"></o-tab-item>
                    <o-tab-item value="sector" label="Sector"></o-tab-item>
                    <o-tab-item v-if="dashboard" value="capability" label="Capability"></o-tab-item>
                </o-tabs>
                <o-notification variant="warning is-light" v-if="view == 'reportGroup' || view == 'practiceGroup' || view == 'subsector' || view == 'industryGroup' || view == 'sector' || view == 'capability'">
                    Industry and function codes may be shared across multiple groups. Therefore the sum of the
                    subtotals below may exceed the Total Billings shown above.
                </o-notification>
            </div>

            <div v-if="breakdownResults && breakdownResults.length > 0">
                <o-table class="table-full-width" paginated :per-page="100" pagination-simple :data="filteredResults" :default-sort="['totalBillings', 'desc']">
                    <o-table-column label="Name" field="name" sortable v-slot="props" v-if="view != 'assignmentId'">
                        <span v-if="view == 'assignmentId'">
                            <a :href="'/mcl/assignments/' + props.row.name" target="_blank">
                                {{ props.row.name }}
                            </a>
                        </span>
                        <span v-else-if="view == 'company'">
                            <a :href="'/mcl/companies/' + props.row.companyId" target="_blank">
                                {{ props.row.name }}
                                <span v-if="props.row.isKeyAccount"><i class="fal fa-key theme-key"></i></span>
                                <span v-if="hasActivePPA(props.row)"><i class="fal fa-file-alt theme-ppa"></i></span>
                            </a>
                        </span>
                        <span v-else-if="view == 'parent'">
                            <a :href="'/mcl/companies/' + props.row.parentId" target="_blank">
                                {{ props.row.name }}
                                <span v-if="props.row.isParentKeyAccount"><i class="fal fa-key theme-key"></i></span>
                                <span v-if="hasParentActivePPA(props.row)"><i class="fal fa-file-alt theme-ppa"></i></span>
                            </a>
                        </span>
                        <span v-else-if="view == 'region' || view == 'license' || view == 'location'">
                            <a href="javascript://" @click="SetBreakdown(props.row)">
                                {{ props.row.name }}
                            </a>
                        </span>
                        <span v-else-if="view == 'fullName'">
                            <a :href="'/directory/boydenians/' + props.row.userId" target="_blank">
                                {{ props.row.name }}
                            </a>
                        </span>
                        <span v-else>
                            {{ props.row.name }}
                        </span>
                    </o-table-column>
                    <o-table-column label="Assignment Name" field="jobTitle" sortable v-slot="props" v-if="view == 'assignmentId'">
                        <a :href="'/mcl/assignments/' + props.row.name" target="_blank">
                            {{ props.row.jobTitle }}
                        </a>
                    </o-table-column>
                    <o-table-column label="Company" field="company" sortable v-slot="props" v-if="view == 'assignmentId'">
                        <a :href="'/mcl/companies/' + props.row.companyId" target="_blank">
                            {{ props.row.company }}
                        </a>
                    </o-table-column>
                    <o-table-column label="Total Billings" field="totalBillings" sortable v-slot="props">
                        {{ $filters.Currency($store.getters.user.Currency, props.row.totalBillings) }}
                    </o-table-column>
                    <o-table-column label="Teaming Value" field="teamingValue" sortable v-slot="props">
                        {{ $filters.Currency($store.getters.user.Currency, props.row.teamingValue) }}
                    </o-table-column>
                    <o-table-column label="Teaming Revenue" field="teamingRevenue" sortable v-slot="props">
                        {{ $filters.Currency($store.getters.user.Currency, props.row.teamingRevenue) }}
                    </o-table-column>
                    <o-table-column label="Teaming Taken" field="teamingTaken" sortable v-slot="props">
                        {{ nformat(props.row.teamingTaken, 2) }}
                    </o-table-column>
                    <o-table-column label="Teaming Filled" field="teamingFilled" sortable v-slot="props">
                        {{ nformat(props.row.teamingFilled, 2) }}
                    </o-table-column>
                    <o-table-column label="Assignments Taken" field="assignmentsTaken" sortable v-slot="props">
                        {{ nformat(props.row.assignmentsTaken, 2) }}
                    </o-table-column>
                    <o-table-column label="Assignments Filled" field="assignmentsFilled" sortable v-slot="props">
                        {{ nformat(props.row.assignmentsFilled, 2) }}
                    </o-table-column>
                    <o-table-column label="Time to Fill" field="timeToFill" sortable v-slot="props">
                        {{ nformat(props.row.timeToFill, 2) }}
                    </o-table-column>
                    <o-table-column label="Average Filled Job Value" field="filledJobValue" sortable v-slot="props" v-if="showFilledJobValue">
                        {{ $filters.Currency($store.getters.user.Currency, props.row.filledJobValue) }}
                    </o-table-column>
                </o-table>
            </div>
            <div v-else class="has-text-centered py-5">
                No data available for this breakdown.
            </div>
        </template>
    </card>
</template>
<script>
import { ref, inject, computed, onMounted, onBeforeMount } from "vue";
import { GenericReport, Breakdown, BreakdownList } from "@/assets/types";
import { currencyFormat, numberFormat } from "@/services/utils";
import breadcrumb from "@/components/webComponents/breadcrumb/breadcrumb.vue";
import store from "@/store";
import { breakdown_output } from "./breakdownOutput.js";
import { breakdownTypes, filterBreakdownResults, createBreakdownMetrics } from "@/services/breakdownCalculations";

export default {
    components: {
        breadcrumb
    },
    props: {
        report: {
            type: GenericReport,
            default: () => {
                return new GenericReport("financial");
            }
        },
        dashboard: {
            type: Boolean,
            default: false
        },
        showFilledJobValue: {
            type: Boolean,
            default: false
        }
    },
    emits: ["change"],
    setup(props, context) {
        const $financial = inject("$financial");
        const cformat = ref(currencyFormat.format);
        const nformat = ref(numberFormat.format);

        const currentBreakdown = ref(new Breakdown());
        const view = ref("region");
        const search = ref("");
        const options = ref({
            breakdowns: BreakdownList.filter((option) => !props.dashboard || !["practiceGroup", "subsector"].includes(option.key))
        });
        const cache = ref({});
        const hasActivePPA = (row) => row?.hasActivePPA || row?.HasActivePPA || row?.hasActivePpa;
        const hasParentActivePPA = (row) => row?.parentHasActivePPA || row?.ParentHasActivePPA;

        const breadcrumbs = computed(() => {
            let crumbs = [{ fn: Revert, text: "Global" }];
            if (currentBreakdown.value.breakdownFilter) {
                crumbs = crumbs.concat(
                    BreakdownList.filter((x) => {
                        let geo = ["region", "license", "location"];
                        const i = geo.findIndex((y) => y === currentBreakdown.value.breakdown);
                        geo = geo.slice(0, i + 1);
                        if (i > -1) {
                            return geo.some((y) => y === x.key);
                        } else {
                            return x.key === currentBreakdown.value.breakdown;
                        }
                    }).map((x) => {
                        const geo = ["region", "license", "location"];
                        if (geo.some((y) => y === x.key)) {
                            x.fn = () => {
                                ResetCrumb(x);
                            };
                        }
                        return x;
                    })
                );
                crumbs.push({ text: currentBreakdown.value.breakdownFilter });
            }

            return crumbs;
        });

        const ResetCrumb = (crumb) => {
            const first = props.report.results.find(
                (x) => x[currentBreakdown.value.breakdown] === currentBreakdown.value.breakdownFilter
            );
            currentBreakdown.value.breakdown = crumb.key;
            currentBreakdown.value.breakdownFilter = first[crumb.key];
            if (crumb.key) {
                const i = BreakdownList.findIndex((x) => x.key === crumb.key);
                view.value = BreakdownList[i + 1].key;
            }
        };

        const BreakdownFilterResults = (results) =>
            filterBreakdownResults(results, currentBreakdown.value, store.getters.reportGroups, options.value.view);

        const hasReportResults = computed(() => {
            return (props.report?.results?.length || 0) > 0;
        });

        const breakdownResults = computed(() => {
            const filteredResults = BreakdownFilterResults(props.report.results);
            const metrics = CreateMetrics(filteredResults, { view: view.value });
            if (!currentBreakdown.value.breakdownFilter) {
                breakdown_output.value[view.value] = metrics;
            }
            return metrics;
        });

        const filteredResults = computed(() => {
            if (search.value.length > 0) {
                return breakdownResults.value.filter((x) => x.name.toLowerCase().includes(search.value.toLowerCase()));
            } else {
                return breakdownResults.value;
            }
        });

        const CreateMetrics = (filteredResults, options) => {
            const name = options?.view || currentBreakdown.value.breakdown;
            const breakdownFilter = currentBreakdown.value.breakdownFilter;
            if (!options?.view) {
                if (cache.value[name + breakdownFilter] === undefined && filteredResults.length > 0) {
                    cache.value[name + breakdownFilter] = {};
                }

                if (cache.value[name + breakdownFilter]) {
                    if (options?.compare && cache.value[name + breakdownFilter]?.compare) {
                        return cache.value[name + breakdownFilter].compare;
                    } else if (!options?.compare && cache.value[name + breakdownFilter]?.results) {
                        return cache.value[name + breakdownFilter].results;
                    }
                }
            }

            const results = createBreakdownMetrics(filteredResults, {
                breakdown: currentBreakdown.value,
                reportGroups: store.getters.reportGroups,
                financial: $financial,
                options,
                dashboard: props.dashboard,
                groupFilters: props.report.filter?.keys || []
            });

            if (!options?.view) {
                if (cache.value[name + breakdownFilter]) {
                    if (options?.compare) {
                        cache.value[name + breakdownFilter].compare = results;
                    } else {
                        cache.value[name + breakdownFilter].results = results;
                    }
                }
            }

            return results;
        };

        const Search = (text) => {
            search.value = text;
        };

        const SwitchView = (v) => {
            view.value = v;
        };

        const TryAdvance = (row) => {
            if (["region", "license", "location"].some((i) => i === view.value)) {
                view.value = BreakdownList.at(BreakdownList.findIndex((x) => x.key == view.value) + 1).key;
            }
        };

        const EmitApi = () => {
            context.emit("change", {
                breakdown: currentBreakdown.value,
                api: {
                    BreakdownFilterResults: BreakdownFilterResults,
                    CreateMetrics: CreateMetrics,
                    ResetCache: ResetCache,
                    BreakdownTypes: breakdownTypes.filter((option) => !props.dashboard || !["practiceGroup", "subsector"].includes(option.key)),
                    Revert: Revert //Revert breakdown filter
                }
            });
        };

        const SetBreakdown = (row) => {
            currentBreakdown.value.breakdown = view.value;
            currentBreakdown.value.breakdownFilter = row.name;
            TryAdvance(row);
            search.value = "";
            EmitApi();
        };

        const Revert = () => {
            currentBreakdown.value.breakdown = "region";
            currentBreakdown.value.breakdownFilter = "";
            view.value = "region";
        };

        // const GoBack = () => {
        // 	//TBA
        // };

        const ResetCache = () => {
            for (const prop in cache.value) delete cache.value[prop];
        };

        onBeforeMount(() => {
            EmitApi();
        });

        onMounted(() => {
            //
        });

        return {
            cformat,
            nformat,
            currentBreakdown,
            view,
            search,
            options,
            breadcrumbs,
            breakdownResults,
            hasReportResults,
            filteredResults,
            Search,
            SwitchView,
            SetBreakdown,
            Revert,
            hasActivePPA,
            hasParentActivePPA
        };
    }
};
</script>
<style lang="scss" scoped>
.selections {
    &>div {
        border: 1px solid #0d0d0d;
        border-radius: 5px;
        padding: 2px 5px;

        &.selected {
            background-color: #dadada;

            &:hover {
                background-color: #dadadaaa;
            }
        }

        &:hover {
            cursor: pointer;
            background-color: #dadadaaa;
        }
    }
}

.tab-section .b-tabs {
    margin-bottom: 0px;
}
</style>
